import { useNavigate, useSearchParams } from "react-router";
import { AlertTriangle, ArrowRight, Briefcase, FolderOpen, Inbox } from "lucide-react";
import { formatDate, formatDateTime, formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, CollapsibleSection, DeadlineKey, EmptyState, PageHeader, Skeleton, StackedBars, Tabs, TabsContent, TabsList, TabsTrigger, Timeline, cn, SourceText } from "@ncp/ui";
import { useDashboard, useMyWork, type WorkItem } from "../features/commandCenter/api";
import { WorkList } from "../features/commandCenter/WorkList";
import { InboxPage } from "./InboxPage";
import { useStaffName } from "../lib/staff-names";
import { RiskLevel } from "../components/RiskLevel";
import { PeriodRing } from "../components/PeriodRing";
import { EC_PHASE_ORDER, stageOf } from "../features/case/process/phases";

function Panel({ title, meta, actions, children, className }: { title: React.ReactNode; meta?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; className?: string }) {
  return (
    <Card className={cn("flex min-w-0 flex-col", className)}>
      <CardHeader className="flex flex-row items-baseline justify-between gap-3 space-y-0 p-5 pb-3">
        <div className="min-w-0">
          <CardTitle className="text-base font-semibold">{title}</CardTitle>
          {meta ? <p className="mt-0.5 text-xs text-muted-foreground">{meta}</p> : null}
        </div>
        {actions}
      </CardHeader>
      <CardContent className="flex-1 p-5 pt-0">{children}</CardContent>
    </Card>
  );
}

/** Unassigned high-priority files, brought to the supervisor's attention before anything else. */
function UnassignedAlert({ items }: { items: WorkItem[] }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  if (items.length === 0) return null;
  return (
    <div role="alert" className="rounded-lg border border-danger-200 bg-danger-50/60 p-4">
      <p className="flex items-center gap-2 text-sm font-semibold text-danger-800"><AlertTriangle className="size-4" /> {t("backoffice:commandCenter.unassignedAlert", { count: items.length })}</p>
      <ul className="mt-2 divide-y divide-danger-100">
        {items.map((w) => (
          <li key={w.caseId} className="flex items-center gap-3 py-1.5 text-sm">
            <button type="button" onClick={() => navigate(`/cases/${w.caseId}`)} className="min-w-0 flex-1 text-start hover:underline">
              <span className="line-clamp-1"><SourceText text={locale === "ar" && w.titleAr ? w.titleAr : w.title} /></span>
              <bdi className="font-mono text-xs text-danger-800">{w.caseNumber}</bdi>
            </button>
            <Button size="sm" variant="outline" className="h-7 shrink-0 bg-white px-2.5 text-xs" onClick={() => navigate(`/cases/${w.caseId}`)}>{t("backoffice:commandCenter.assign")}</Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The officer's starting point: their own files with the next step and the nearest deadline; for supervisors, what awaits their decision,
 * approaching deadlines and the workload. Every list says its order; every count is the number of rows beneath it. The Department-wide
 * figures live on the Statistics page.
 */
export function CommandCenterPage() {
  const { t } = useTranslation();
  const staffName = useStaffName();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "inbox" ? "inbox" : "work";
  const work = useMyWork();
  const dash = useDashboard();

  const setView = (v: string) => setParams((p) => {
    if (v === "inbox") p.set("view", "inbox");
    else { p.delete("view"); p.delete("agent"); p.delete("proposal"); p.delete("status"); p.delete("run"); }
    return p;
  });

  // Merger files where they stand today, in the six stages of the file's own stage bar.
  const byStage = EC_PHASE_ORDER.map((key) => ({
    key,
    count: Object.entries(dash.data?.ecByState ?? {}).filter(([state]) => stageOf("EconomicConcentration", state) === key).reduce((n, [, c]) => n + c, 0),
  }));
  const maxStage = Math.max(1, ...byStage.map((s) => s.count));
  // Open files, nearest statutory deadline first; files without a running period follow in register order.
  const watchlist = [...(dash.data?.watchlist ?? [])].sort((a, b) => (a.primaryClock?.dueAt ?? "9999").localeCompare(b.primaryClock?.dueAt ?? "9999"));

  return (
    <div className="space-y-6">
      <PageHeader title={t("backoffice:commandCenter.title")} className="mb-2" />
      <Tabs value={view} onValueChange={setView}>
        <TabsList variant="underline" className="mb-4">
          <TabsTrigger value="work" variant="underline"><Briefcase /> {t("backoffice:commandCenter.myWork")}</TabsTrigger>
          <TabsTrigger value="inbox" variant="underline"><Inbox /> {t("nav.aiInbox")} {work.data?.pendingProposals ? <Badge variant="outline" size="sm">{formatNumber(work.data.pendingProposals, locale)}</Badge> : null}</TabsTrigger>
        </TabsList>

        <TabsContent value="work" className="space-y-6">
          {work.isLoading || !work.data ? <Skeleton className="h-72" /> : null}
          {work.data?.supervisor ? <UnassignedAlert items={work.data.unassignedPriority} /> : null}
          {work.data?.supervisor ? (
            <section className="grid items-start gap-6 xl:grid-cols-2">
              <Panel title={t("backoffice:commandCenter.awaitingDecision")} meta={t("backoffice:commandCenter.nearestFirst")}>
                <WorkList items={work.data.awaitingDecision} empty={t("backoffice:commandCenter.nothingAwaiting")} showAssignee />
              </Panel>
              <Panel title={t("backoffice:commandCenter.approaching")} meta={t("backoffice:commandCenter.nearestFirst")}>
                <WorkList items={work.data.approaching} empty={t("backoffice:commandCenter.nothingApproaching")} showAssignee />
              </Panel>
            </section>
          ) : null}
          {work.data && !work.data.supervisor ? (
            <Panel title={t("backoffice:commandCenter.myFiles")} meta={t("backoffice:commandCenter.nearestFirst")}>
              <WorkList items={work.data.myCases} empty={t("backoffice:commandCenter.noFiles")} />
            </Panel>
          ) : null}

          {dash.data ? (
            <>
              <Panel title={t("backoffice:commandCenter.watchlist")} meta={t("backoffice:commandCenter.watchlistOrder")} actions={<Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => navigate("/concentration")}>{t("backoffice:commandCenter.openRegister")} <ArrowRight className="rtl:rotate-180" /></Button>}>
                {watchlist.length === 0 ? <EmptyState icon={FolderOpen} title={t("misc.noResults")} className="border-0 bg-transparent py-8" /> : (
                  <>
                    <table className="w-full table-fixed text-sm">
                      <thead>
                        <tr className="border-b border-sand-200 text-start text-xs text-sand-700">
                          <th className="py-2 pe-3 text-start font-medium">{t("backoffice:cases.columns.title")}</th>
                          <th className="hidden w-28 px-3 py-2 text-start font-medium md:table-cell">{t("backoffice:cases.columns.stage")}</th>
                          <th className="hidden w-44 px-3 py-2 text-start font-medium lg:table-cell">{t("backoffice:cases.columns.assignee")}</th>
                          <th className="w-40 px-3 py-2 text-start font-medium">{t("backoffice:cases.columns.period")}</th>
                          <th className="hidden w-24 py-2 ps-3 text-end font-medium sm:table-cell">{t("backoffice:cases.columns.risk")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {watchlist.map((w) => (
                          <tr key={w.caseId} onClick={() => navigate(`/cases/${w.caseId}`)} className="cursor-pointer border-b border-sand-100 align-top last:border-0 hover:bg-sand-50">
                            <td className="py-2.5 pe-3">
                              <p className="line-clamp-2 font-medium text-aeblack-950"><SourceText text={locale === "ar" && w.titleAr ? w.titleAr : w.title} /></p>
                              <p className="mt-0.5 font-mono text-xs text-muted-foreground"><bdi>{w.caseNumber}</bdi></p>
                            </td>
                            <td className="hidden px-3 py-2.5 text-xs md:table-cell">{t(`backoffice:workspace.phases.${stageOf(w.type, w.state)}`)}</td>
                            <td className="hidden px-3 py-2.5 text-xs lg:table-cell">{(locale === "ar" && w.assigneeNameAr) || w.assigneeName || (w.unattended ? <span className="font-medium text-danger-700">{t("backoffice:cases.unattended")}</span> : <span className="text-muted-foreground">{t("misc.unassigned")}</span>)}</td>
                            <td className="px-3 py-2">{w.primaryClock ? <PeriodRing clock={w.primaryClock} /> : <span className="text-xs text-muted-foreground">{t("backoffice:cases.noPeriod")}</span>}</td>
                            <td className="hidden py-2.5 ps-3 text-end sm:table-cell"><RiskLevel score={w.riskScore} type={w.type} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <DeadlineKey className="mt-3" />
                  </>
                )}
              </Panel>

              <Panel title={t("backoffice:commandCenter.byStage")} meta={t("backoffice:commandCenter.byStageMeta")}>
                <ul className="space-y-2">
                  {byStage.map((s) => (
                    <li key={s.key} className="grid grid-cols-[7rem_minmax(0,1fr)_2.5rem] items-center gap-3 text-sm">
                      <span>{t(`backoffice:workspace.phases.${s.key}`)}</span>
                      <span className="h-2 rounded-full bg-sand-100"><span className="block h-2 rounded-full bg-aeblack-600" style={{ width: `${(s.count / maxStage) * 100}%` }} /></span>
                      <span className="text-end tabular">{formatNumber(s.count, locale)}</span>
                    </li>
                  ))}
                </ul>
              </Panel>
            </>
          ) : work.data ? <Skeleton className="h-64" /> : null}

          {work.data?.supervisor ? (
            <section className="grid items-start gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <Panel title={t("backoffice:commandCenter.myFiles")} meta={t("backoffice:commandCenter.nearestFirst")}>
                <WorkList items={work.data.myCases} empty={t("backoffice:commandCenter.noFiles")} />
              </Panel>
              {work.data.workload.length > 0 ? (
                <Panel title={t("backoffice:commandCenter.workload")}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-sand-200 text-xs text-sand-700">
                        <th className="py-2 text-start font-medium">{t("backoffice:cases.columns.assignee")}</th>
                        <th className="py-2 text-end font-medium">{t("backoffice:commandCenter.workloadOpen")}</th>
                        <th className="py-2 text-end font-medium">{t("backoffice:commandCenter.workloadAtRisk")}</th>
                        <th className="py-2 text-end font-medium">{t("backoffice:commandCenter.workloadPast")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {work.data.workload.map((w) => (
                        <tr key={w.userId ?? "unassigned"} className="border-b border-sand-100 last:border-0">
                          <td className="py-2">{(locale === "ar" && w.nameAr) || w.name || <span className="text-warning-700">{t("misc.unassigned")}</span>}</td>
                          <td className="py-2 text-end tabular">{formatNumber(w.open, locale)}</td>
                          <td className={cn("py-2 text-end tabular", w.atRisk > 0 ? "text-warning-700" : "text-muted-foreground")}>{formatNumber(w.atRisk, locale)}</td>
                          <td className={cn("py-2 text-end tabular", w.breached > 0 ? "font-semibold text-danger-700" : "text-muted-foreground")}>{formatNumber(w.breached, locale)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              ) : null}
            </section>
          ) : null}

          {dash.data ? (
            <div className="space-y-3">
              <CollapsibleSection title={t("backoffice:commandCenter.intake")}>
                <StackedBars
                  series={(() => {
                    const keys = Array.from(new Set(dash.data.intakeTrend.flatMap((p) => Object.keys(p.byType)))).filter((k) => k !== "Generic");
                    return (keys.length ? keys : ["EconomicConcentration"]).map((k) => ({ name: t(`caseTypes.${k}`, { defaultValue: k }), data: dash.data!.intakeTrend.map((p) => p.byType[k] ?? 0) }));
                  })()}
                  categories={dash.data.intakeTrend.map((p) => (/^\d{4}-\d{2}-\d{2}$/.test(p.week) ? formatDate(p.week, locale, "d MMM") : p.week))}
                  height={260}
                />
              </CollapsibleSection>
              <CollapsibleSection title={t("backoffice:commandCenter.recentActivity")}>
                <Timeline items={dash.data.recentActivity.map((e) => ({ id: e.id, title: <span><button type="button" className="font-mono text-xs text-aegold-800 hover:underline" onClick={() => navigate(`/cases/${e.caseId}`)}><bdi>{e.caseNumber}</bdi></button> · {locale === "ar" && e.titleAr ? <SourceText text={staffName(e.titleAr)} /> : e.title}</span>, time: formatDateTime(e.occurredAt, locale), kind: e.actorKind === "Agent" ? "agent" : e.actorKind === "System" ? "system" : e.kind === "StateChanged" ? "milestone" : "human", actor: (locale === "ar" && e.actorNameAr) || (e.actorName ? staffName(e.actorName) : undefined) }))} />
              </CollapsibleSection>
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="inbox"><InboxPage embedded /></TabsContent>
      </Tabs>
    </div>
  );
}

